import React, { Component } from "react";
import PropTypes from "prop-types";
import _throttle from "lodash/throttle";
import styled from "styled-components";
import moment from "moment";

import { H2 } from "./styled/H2";
import { Button } from "./styled/Button";
import { now } from "./NuDateUtils";
import { isPrintMode } from "./QueryParameter";

const TIMED_OUT_MSG = "Your account has been logged out due to inactivity";
const MSG_SEVERITY = "INFO";
const MILLIS_IN_SECOND = 1000;
const SECONDS_TILL_TICKER = 10;
const MILLIS_TILL_TICKER = MILLIS_IN_SECOND * SECONDS_TILL_TICKER;
const MINUTES_UNTIL_LOGOUT = 14;
const MINUTES_UNTIL_WARN = MINUTES_UNTIL_LOGOUT - 1;
const EVENTS = ["load", "click", "keypress"];

export class AutoLogout extends Component {
    state = {
        warningModalShown: false
    };

    componentDidMount() {
        if (!isPrintMode()) {
            const keepAliveDelay = 30000;
            this.throttledKeepAlive = _throttle(this.keepAlive, keepAliveDelay);
            this.lastActionTime = now();

            EVENTS.forEach(e => window.addEventListener(e, this.resetTimeout));
            this.startTimeout();
        }
    }

    componentWillUnmount() {
        EVENTS.forEach(e => window.removeEventListener(e, this.resetTimeout));
        this.stopTimeout();
    }

    keepAlive = failCb =>
        fetch("/nucleus-nuclear/nui/public/keepalive", {
            credentials: "same-origin",
            method: "GET"
        })
            .then(r => {
                if (r.ok) {
                    return r;
                }
                throw new Error(r);
            })
            .then(r => r.json())
            .then(json => !json.username && this.forceLogout())
            .catch(() => failCb());

    displayWarning = shown => this.setState({ warningModalShown: shown });

    forceLogout = message => {
        this.stopTimeout();
        this.props.logout({ message, severity: MSG_SEVERITY });
    };

    resetTimeout = () => {
        this.lastActionTime = now();

        this.throttledKeepAlive(() => {});
        this.stopTimeout();
        this.startTimeout();
    };

    startTimeout = () => {
        this.ticker = setTimeout(
            () => this.tickerAction(this.lastActionTime, now()),
            MILLIS_TILL_TICKER
        );
    };

    stopTimeout = () => {
        if (this.ticker) {
            this.displayWarning(false);
            clearTimeout(this.ticker);
        }
    };

    tickerAction = (lastActionTime, now) => {
        const minutesSinceLastAction = moment.duration(now.diff(lastActionTime)).asMinutes();
        if (minutesSinceLastAction >= MINUTES_UNTIL_LOGOUT) {
            return this.forceLogout(TIMED_OUT_MSG);
        }
        if (minutesSinceLastAction >= MINUTES_UNTIL_WARN) {
            this.displayWarning(true);
        }
        this.startTimeout();
    };

    render() {
        return (
            <Modal data-testid="auto-logout" shown={this.state.warningModalShown}>
                <ModalMain>
                    <H2 textAlign="center">
                        You are about to be logged out due to inactivity, would you like to
                        continue?
                    </H2>
                    <Button onClick={this.resetTimeout}>Continue</Button>
                </ModalMain>
            </Modal>
        );
    }
}

AutoLogout.propTypes = {
    logout: PropTypes.func.isRequired
};

export const Modal = styled.div`
    ${props => !props.shown && "display: none;"}
    z-index: 999;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
`;

const ModalMain = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    position: fixed;
    background: white;
    width: 50%;
    min-height: 20vh;
    top: 25%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 1em;

    @media (max-width: ${props => props.theme.maxWidth}) {
        width: 95%;
    }
`;
